import { internationalizedField } from '~/schemas/common'
import { onlyPublished, sanityClientCdn } from '~/services/sanity'
import type { SiteSettings } from '~/types/siteSettings'

export type TMetaTemplate =
  | 'article'
  | 'defaults'
  | 'artist'
  | 'genre'
  | 'artwork'
  | 'collection'
  | 'page'
  | 'team'

export enum MetaTemplate {
  Article = 'article',
  Defaults = 'defaults',
  Artist = 'artist',
  Genre = 'genre',
  Artwork = 'artwork',
  Collection = 'collection',
  Page = 'page',
  Team = 'team',
}

export const metaTemplateArr = [
  'article',
  'defaults',
  'artist',
  'genre',
  'artwork',
  'collection',
  'page',
  'team',
]

// Document types that support field-level translation (matches CMS schema)
export const FIELD_LEVEL_TRANSLATED_DOCUMENT_TYPES = [
  'artist',
  'artwork',
  'category',
  'collection',
  'genre',
  'team',
]

export const getSEOMetaByType = async (
  type: TMetaTemplate,
  data: any,
  siteSettings: SiteSettings,
  pageNumber?: string | undefined,
  lang = 'en'
) => {
  // Filter out null/undefined values from data.meta to avoid overriding templates
  const cleanedMeta =
    data.meta && typeof data.meta === 'object'
      ? Object.fromEntries(
          Object.entries(data.meta).filter(([_, value]) => value != null)
        )
      : data.meta

  const metaTemplates = Object.assign(
    {},
    siteSettings.seo.metaTemplates[type],
    cleanedMeta
  )

  try {
    return await resolveMeta(data._id, metaTemplates, pageNumber, lang)
  } catch {
    return {}
  }
}

export const metaQuery = (documentType?: string) => {
  const requiresTranslation =
    documentType && FIELD_LEVEL_TRANSLATED_DOCUMENT_TYPES.includes(documentType)

  if (requiresTranslation) {
    return `"meta": {
      "title": ${internationalizedField('meta.title')},
      "description": ${internationalizedField('meta.description')},
      "image": meta.image,
      showInSitemap,
      noIndexPage
    }, _id`
  }

  return `meta, _id`
}

export async function resolveMeta(
  id: string,
  templates: Record<'title' | 'description' | 'image', string> & {
    noIndexPage?: boolean
  },
  pageNumber?: string | undefined,
  lang = 'en'
) {
  if (!id) return templates
  if (templates.image && typeof templates.image === 'object') {
    templates.image = `"image": *[_id == "${
      (templates.image as any).asset._ref
    }"]`
  }

  const projection = createProjection(
    [
      templates.title,
      templates.description,
      templates.image || '${image}',
    ].filter(Boolean)
  )

  const data = await sanityClientCdn.fetch<Record<string, string>>(
    `*[_id == $id ${onlyPublished}][0] { ${projection} }`,
    { id, lang }
  )

  const image = data.image as unknown as Record<string, any>

  const paged = pageNumber ? `Page ${pageNumber} ` : ``

  return {
    title: `${paged}${interpolateString(`${templates.title}`, data)}`,
    description: interpolateString(templates.description, data),
    noIndexPage:
      templates?.noIndexPage && templates?.noIndexPage === true ? true : false,
    images: image?.url
      ? [
          // Facebook / LinkedIn. `fit=crop` is required: passing w+h alone
          // leaves Sanity free to preserve the source aspect ratio.
          {
            url: `${image.url}?w=1200&h=630&fit=crop&q=80&fm=jpg`,
            width: 1200,
            height: 630,
          },
          // Twitter
          {
            url: `${image.url}?w=1080&h=1080&fit=crop&q=80&fm=jpg`,
            width: 1080,
            height: 1080,
          },
          // Instagram
          {
            url: `${image.url}?w=1200&h=675&fit=crop&q=80&fm=jpg`,
            width: 1200,
            height: 675,
          },
        ]
      : [],
  }
}

const magicTemplates: Record<string, string> = {
  artworksByArtist:
    '"artworksByArtist": count(*[_type == "artwork" && artist._ref == coalesce(^.artist._ref, ^._id)])',
  totalListedArtworks: '"totalListedArtworks": count(*[_type == "artwork"])',
  totalArtistArtworks: `"totalArtistArtworks": count(*[_type == "artwork" && artist._ref == ^._id && !(_id in path('drafts.**')) && isVisible == true && _isDeleted != true])`,
  artworksByGenre:
    '"artworksByGenre": count(*[_type == "artwork" && genre._ref == coalesce(^.genre._ref, ^._id)])',
  image: '"image": image.asset->',
  artworkConditionalYear:
    '"artworkConditionalYear": *[_type == "artwork" && defined(isDuplicatedTitle) && _id == ^._id][0].year',
  artworkConditionalMedium:
    '"artworkConditionalMedium": *[_type == "artwork" && defined(isDuplicatedTitle) && _id == ^._id][0].medium->name',
}

function createProjection(templates: string[]) {
  const keys = new Set<string>()
  for (const template of templates) {
    const matches = Array.from(template.matchAll(/\$\{([^}]+)\}/g))
    for (const match of matches) {
      keys.add(match[1])
    }
  }

  return Array.from(keys)
    .map(k => {
      return magicTemplates[k]
        ? magicTemplates[k]
        : k === 'format'
          ? `"${k}": ${k}Description->name`
          : `"${k}": coalesce(${k}->title, ${k}->name, ${k})`
    })
    .join(', ')
}

function interpolateString(text = '', data: Record<string, string>) {
  return text
    .replace(/\$\{([^}]+)\}/g, (_, key) => {
      return data[key] || ''
    })
    .replace(/&nbsp;/g, ' ') // Convert HTML non-breaking spaces to regular spaces
    .replace(/\s\s+/g, ' ') // Collapse multiple spaces
    .trim() // Remove leading/trailing whitespace
}
